import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProcessSection from '@/components/feature/ProcessSection';
import WhyChooseUs from '@/components/feature/WhyChooseUs';
import ServiceAreas from '@/components/feature/ServiceAreas';
import Testimonials from '@/components/feature/Testimonials';
import FAQSection from '@/components/feature/FAQSection';
import FinalCTA from '@/components/feature/FinalCTA';
import MidPageCTA from '@/components/feature/MidPageCTA';
import HeroSection from '@/pages/home/components/HeroSection';
import AIAnswerBlock from '@/pages/home/components/AIAnswerBlock';
import ServicesOverview from '@/pages/home/components/ServicesOverview';

const localAIQA = [
  {
    q: 'Who offers window tinting in Philadelphia?',
    a: 'TINTSPRO X provides professional window tinting for all vehicles, homes, and businesses in Philadelphia, PA. Located at 3001 Ormes St Unit 05, they serve all Philadelphia neighborhoods with premium film and expert installation.',
  },
  {
    q: 'What tinting services are available in Philadelphia?',
    a: 'Philadelphia clients can access automotive tinting, commercial tinting, residential tinting, ceramic tinting, carbon fiber installation, and window film services — all installed by the trained local team at TINTSPRO X.',
  },
  {
    q: 'How fast can TINTSPRO X tint my car in Philadelphia?',
    a: 'As a Philadelphia-based studio at 3001 Ormes St, TINTSPRO X typically offers same-day or next-day appointments for automotive window tinting, with residential and commercial tinting scheduled to fit your timeline.',
  },
  {
    q: 'Do you serve areas outside Center City Philadelphia?',
    a: 'Yes. TINTSPRO X serves all Philadelphia neighborhoods including Northeast Philly, South Philadelphia, Kensington, Fishtown, and surrounding townships in Montgomery County, Bucks County, and South Jersey.',
  },
];

const localWhyItems = [
  { icon: 'ri-map-pin-fill', text: 'Philadelphia-Based Studio', sub: 'Local team, fast scheduling — based at 3001 Ormes St Unit 05, Philadelphia.' },
  { icon: 'ri-sun-line', text: 'Built for Philadelphia Climate', sub: 'We know how intense Philadelphia summers get — our films are selected for maximum heat rejection.' },
  { icon: 'ri-time-fill', text: 'Same-Day Tinting Available', sub: 'Most automotive tint jobs completed same-day. Residential and commercial projects scheduled at your convenience.' },
  { icon: 'ri-community-line', text: 'Trusted by Philly Clients', sub: 'Serving Philadelphia vehicles, homes, and businesses with 5-star results for over a decade.' },
];

const localFAQs = [
  {
    q: 'Do you offer window tinting services in Philadelphia?',
    a: 'Yes. TINTSPRO X provides professional automotive, residential, and commercial window tinting throughout Philadelphia, PA and surrounding areas. Same-day appointments are often available at their 3001 Ormes St studio.',
  },
  {
    q: 'What are the tint laws in Pennsylvania?',
    a: 'Pennsylvania law requires front side windows to allow at least 70% light transmission. Rear side windows and back windshield can be tinted to any level. TINTSPRO X ensures all automotive installations meet PA legal requirements.',
  },
  {
    q: 'Is residential window tinting worth it in Philadelphia?',
    a: 'Absolutely. Philadelphia summers bring intense UV exposure that fades furniture and heats interiors. Residential window film blocks up to 80% of solar heat and UV rays, protecting furnishings while reducing energy costs year-round.',
  },
  {
    q: 'Can I get a free window tinting estimate in Philadelphia?',
    a: 'Yes. Contact TINTSPRO X for a free, no-obligation quote on any window tinting service in Philadelphia. Reach them at (215) 594-9404, tintspro2@gmail.com, or through the estimate form on this page.',
  },
];

const localAreas = [
  'Philadelphia, PA',
  'Northeast Philadelphia',
  'South Philadelphia',
  'Kensington',
  'Fishtown',
  'Port Richmond',
  'Mayfair',
  'Frankford',
  'Roxborough',
  'Manayunk',
  'Cheltenham Township',
  'Bensalem Township',
  'Levittown, PA',
  'Bristol Township',
  'Lansdale',
  'Ambler',
  'Pennsauken, NJ',
  'Cherry Hill, NJ',
  'Moorestown, NJ',
];

const localServices = [
  {
    id: 'automotive-tinting', title: 'Automotive Tinting in Philadelphia',
    desc: 'Professional automotive window tinting for Philadelphia drivers — premium film, expert installation, same-day availability.',
    icon: 'ri-car-fill', tag: 'Most Popular',
    image: 'https://public.readdy.ai/ai/img_res/c725cfa5c0a2e0ded472ff73ea5b18d0.jpg',
  },
  {
    id: 'commercial-tinting', title: 'Commercial Tinting in Philadelphia',
    desc: 'Window film for Philadelphia offices, storefronts, and commercial buildings — reduce glare and improve energy efficiency.',
    icon: 'ri-building-line',
    image: 'https://readdy.ai/api/search-image?query=commercial%20building%20window%20tinting%20film%20application%20modern%20office%20Philadelphia%20glass%20storefront%20exterior%20urban%20glare%20reduction%20energy%20savings%20professional%20commercial%20tint&width=600&height=500&seq=loc-svc-2&orientation=landscape',
  },
  {
    id: 'residential-tinting', title: 'Residential Tinting in Philadelphia',
    desc: 'Home window film for Philadelphia residents — block UV, reduce heat, protect furnishings, and enhance privacy.',
    icon: 'ri-home-4-line',
    image: 'https://readdy.ai/api/search-image?query=residential%20home%20window%20tinting%20film%20Philadelphia%20modern%20house%20bright%20interior%20natural%20light%20UV%20protection%20privacy%20home%20window%20film%20professional%20application&width=600&height=500&seq=loc-svc-3&orientation=landscape',
  },
  {
    id: 'ceramic-tinting', title: 'Ceramic Tinting in Philadelphia',
    desc: 'Top-tier ceramic film with maximum heat and UV rejection — no signal interference and long-lasting performance.',
    icon: 'ri-contrast-drop-2-line',
    image: 'https://readdy.ai/api/search-image?query=ceramic%20window%20tint%20film%20luxury%20car%20window%20premium%20ceramic%20tinting%20close%20up%20dark%20cinematic%20dramatic%20studio%20dark%20moody%20high%20contrast%20automotive%20Philadelphia%20premium%20tinting&width=600&height=500&seq=loc-svc-4&orientation=landscape',
  },
];

export default function LocationPage() {
  return (
    <div className="bg-[#080808]">
      <Navbar />
      <HeroSection
        eyebrow="Window Tinting in Philadelphia, PA"
        title="Window Tinting in Philadelphia"
        subtitle="Professional automotive, residential, and commercial window tinting in Philadelphia — premium film, expert installation, and fast scheduling from our local studio."
        primaryCTA="Get a Philadelphia Quote"
        imageSrc="https://readdy.ai/api/search-image?query=luxury%20black%20car%20dark%20tinted%20windows%20professional%20tint%20result%20cinematic%20studio%20dramatic%20lighting%20Philadelphia%20automotive%20tinting%20dark%20moody%20background%20high%20contrast%20reflective%20floor%20deep%20shadows%20stunning%20result%20premium%20window%20film&width=1920&height=1080&seq=loc-hero-tint-001&orientation=landscape"
      />

      {/* Local info strip */}
      <div className="bg-[#0f0f0f] border-b border-white/5 py-4 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-fill text-[#AAFF00] text-sm"></i>
            </div>
            <span className="text-white/50 text-sm font-sans">3001 Ormes St Unit 05, Philadelphia, PA 19134</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="tel:2155949404" className="flex items-center gap-2 text-white/50 text-sm font-sans hover:text-[#AAFF00] transition-colors cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-[#AAFF00] text-sm"></i>
              </div>
              (215) 594-9404
            </a>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <div key={s} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-[#AAFF00] text-xs"></i>
                </div>
              ))}
              <span className="text-white/35 text-xs font-sans ml-1">5.0 Google</span>
            </div>
          </div>
        </div>
      </div>

      <ServicesOverview
        heading="Philadelphia Tinting Services"
        subheading="Window tinting for vehicles, homes, and businesses in Philadelphia — same-day availability for automotive tinting."
        services={localServices}
        showCTA={false}
      />
      <MidPageCTA
        heading="Serving Philadelphia Clients Since Day One"
        subheading="Local expertise. Premium film. Same-day automotive tinting available."
        ctaLabel="Get Philadelphia Quote"
        variant="gold"
      />
      <AIAnswerBlock heading="Window Tinting in Philadelphia" items={localAIQA} />
      <ProcessSection />
      <WhyChooseUs
        title="Local Expertise.\nProfessional Tinting Results."
        subtitle="As a Philadelphia-based studio, we understand local conditions, PA tint regulations, and what Philadelphia clients expect from their tinting service."
        items={localWhyItems}
      />
      <ServiceAreas
        heading="Philadelphia & Surrounding Service Areas"
        description="TINTSPRO X provides automotive, residential, and commercial window tinting across Philadelphia and the surrounding region — from residential neighborhoods to suburban communities in PA and South Jersey."
        areas={localAreas}
      />
      <Testimonials />
      <FAQSection heading="Philadelphia Window Tinting FAQs" items={localFAQs} />
      <MidPageCTA
        heading="Ready to Tint in Philadelphia?"
        subheading="Free quotes · Fast scheduling · Professional results"
        ctaLabel="Get Free Quote"
        variant="dark"
      />
      <FinalCTA
        heading="Window Tinting in&#10;Philadelphia, PA"
        subheading="Contact TINTSPRO X today for a free quote on automotive, residential, or commercial tinting."
        buttonLabel="Request Philadelphia Quote"
      />
      <Footer />
    </div>
  );
}
